import { Injectable } from '@angular/core';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  Appointment,
  AppointmentCreator,
  SchedulingEvent,
  stafferToNamedDoc,
  TreatmentPlan,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  ISchedulingEventData,
  PrincipleCreatedBy,
  type IAppointment,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  snapshot,
  type WithRef,
  Firestore,
  undeleteDoc,
  where,
  DocumentReference,
  orderBy,
} from '@principle-theorem/shared';

@Injectable()
export class TreatmentStepsEditorService {
  constructor(private _organisation: OrganisationService) {}

  async enableNextStep(
    patient: WithRef<IPatient>,
    defaultPractitioner: WithRef<IStaffer>,
    practice: WithRef<IPractice>,
    treatmentPlan: WithRef<ITreatmentPlan>,
    actor: PrincipleCreatedBy
  ): Promise<void> {
    let nextStep = await TreatmentPlan.findStepBy(
      treatmentPlan,
      (appointment) => !appointment
    );
    if (!nextStep || nextStep.appointment) {
      nextStep = await TreatmentPlan.addTreatmentStep(treatmentPlan);
    }

    const practitioner = nextStep.practitionerRef
      ? await Firestore.getDoc(nextStep.practitionerRef)
      : defaultPractitioner;

    await this.generateAppointmentForStep(
      patient,
      practitioner,
      practice,
      treatmentPlan,
      nextStep,
      actor
    );
  }

  async generateAppointmentForStep(
    patient: WithRef<IPatient>,
    practitioner: WithRef<IStaffer>,
    practice: WithRef<IPractice>,
    plan: WithRef<ITreatmentPlan>,
    step: WithRef<ITreatmentStep>,
    actor: PrincipleCreatedBy
  ): Promise<WithRef<IAppointment>> {
    if (step.disabledAppointment) {
      return this._restoreDisabledAppointment(
        step,
        step.disabledAppointment,
        practitioner
      );
    }

    const orphanedAppointment = await this._findOrphanedAppointment(
      step,
      patient,
      practitioner
    );
    if (orphanedAppointment) {
      return orphanedAppointment;
    }

    const duration = await snapshot(TreatmentStep.getDuration$(step));
    const appointmentDetails = {
      practice,
      practitioner: stafferToNamedDoc(practitioner),
      treatment: {
        plan,
        step,
      },
      duration,
    };
    const scheduledByPractice = await snapshot(
      this._organisation.practice$.pipe(filterUndefined())
    );
    const schedulingEventData: ISchedulingEventData = {
      reasonSetManually: false,
      scheduledByPractice: scheduledByPractice.ref,
      schedulingConditions: SchedulingEvent.getSchedulingConditions(
        scheduledByPractice.settings.timezone
      ),
    };

    return AppointmentCreator.add(
      practitioner,
      patient,
      appointmentDetails,
      schedulingEventData,
      actor
    );
  }

  async removeAppointmentFromStep(
    step: WithRef<ITreatmentStep>
  ): Promise<void> {
    if (!step.appointment) {
      return;
    }
    await Firestore.saveDoc({
      ...step,
      appointment: undefined,
      disabledAppointment: step.appointment,
    });
    await Firestore.deleteDoc(step.appointment);
  }

  private async _restoreDisabledAppointment(
    step: WithRef<ITreatmentStep>,
    appointmentRef: DocumentReference<IAppointment>,
    practitioner: WithRef<IStaffer>
  ): Promise<WithRef<IAppointment>> {
    const existingAppointment = await Firestore.getDoc(appointmentRef);
    const namedPractitioner = stafferToNamedDoc(practitioner);
    await undeleteDoc(appointmentRef);
    await Firestore.patchDoc(appointmentRef, {
      practitioner: namedPractitioner,
    });
    await Firestore.saveDoc({
      ...step,
      appointment: appointmentRef,
      disabledAppointment: undefined,
    });
    return {
      ...existingAppointment,
      deleted: false,
      practitioner: namedPractitioner,
    };
  }

  private async _findOrphanedAppointment(
    step: WithRef<ITreatmentStep>,
    patient: WithRef<IPatient>,
    practitioner: WithRef<IStaffer>
  ): Promise<WithRef<IAppointment> | undefined> {
    const orphanedAppointment = await Firestore.firstResult(
      Appointment.col(patient),
      where('treatmentPlan.treatmentStep.ref', '==', step.ref),
      where('deleted', '==', true),
      orderBy('updatedAt', 'desc')
    );

    if (!orphanedAppointment) {
      return;
    }

    const namedPractitioner = stafferToNamedDoc(practitioner);
    await undeleteDoc(orphanedAppointment.ref);
    await Firestore.patchDoc(orphanedAppointment.ref, {
      practitioner: namedPractitioner,
    });
    await Firestore.patchDoc(step.ref, {
      appointment: orphanedAppointment.ref,
    });
    return {
      ...orphanedAppointment,
      deleted: false,
      practitioner: namedPractitioner,
    };
  }
}
